import { Area, Bar, CartesianGrid, ComposedChart, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { KpiFormat } from '../types'
import { AXIS_TICK, axisValue, fmtValue, GRID_STROKE, monthLong, monthShort, SERIES, TOOLTIP_STYLE } from '../visuals'

export type SeriesDef = { key: string; label: string; kind: 'line' | 'area' | 'bar' }

type TrendProps = { data: ({ month: string } & Record<string, number | string>)[]; series: SeriesDef[]; format: KpiFormat; height?: number; zeroLine?: boolean }

export function TrendChart({ data, series, format, height = 260, zeroLine }: TrendProps) {
  const bars = series.filter((s) => s.kind === 'bar').length
  return (
    <div style={{ height }} role="img" aria-label={series.map((s) => s.label).join(', ')}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barGap={2} barCategoryGap={bars > 1 ? '22%' : '32%'}>
          <defs>
            {series.map((s, i) => (
              <linearGradient key={s.key} id={`area-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={SERIES[i]} stopOpacity={0.22} />
                <stop offset="100%" stopColor={SERIES[i]} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="3 3" />
          <XAxis dataKey="month" tickFormatter={monthShort} tick={AXIS_TICK} axisLine={false} tickLine={false} minTickGap={8} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={56} tickFormatter={axisValue(format)} />
          {zeroLine ? <ReferenceLine y={0} stroke="var(--muted-foreground)" strokeOpacity={0.4} /> : null}
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            cursor={bars ? { fill: 'var(--muted)', opacity: 0.5 } : { stroke: 'var(--muted-foreground)', strokeDasharray: '3 3' }}
            labelFormatter={(m) => monthLong(String(m))}
            formatter={(v, name) => [fmtValue(Number(v), format), name]}
          />
          {series.map((s, i) => {
            const color = SERIES[i]
            if (s.kind === 'bar') return <Bar key={s.key} dataKey={s.key} name={s.label} fill={color} radius={[4, 4, 0, 0]} maxBarSize={26} stroke="var(--card)" strokeWidth={1} />
            if (s.kind === 'area')
              return <Area key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={color} strokeWidth={2} fill={`url(#area-${s.key})`} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--card)' }} />
            return <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--card)' }} />
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

export function Sparkline({ values, color = SERIES[0] }: { values: number[]; color?: string }) {
  if (values.length < 2) return null
  return (
    <div className="h-8 w-24" aria-hidden>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={values.map((v, i) => ({ i, v }))} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
